import { useCallback, useEffect, useRef, useState } from "react";
import { sanityCheckAnnotations } from "../services/AnnotationsService";
import { useNotification } from "../classes/contexts/NotificationContext";
import { SnackbarType } from "../classes/enums/SnackbarTypeEnum";
import useTranslation from "./i18n/useTranslation";
import { formatLandmarkName } from "../utils/landmarkNameFormatter";

// Maps error IDs to translation keys for titles (395-401) and messages (402-408)
const ERROR_TITLE_KEYS = {
  101: "395", // skeletal_vertical_failure
  102: "396", // skeletal_horizontal_failure
  103: "397", // missing_vertebra
  104: "398", // inter-penetration
  105: "399", // pedicle-inside-vertebral-body
  106: "400", // pedicle-distance-to-vertebral-body
  107: "401", // side-check
};

const ERROR_MESSAGE_KEYS = {
  101: "402",
  102: "403",
  103: "404",
  104: "405",
  105: "406",
  106: "407",
  107: "408",
};

/**
 * Hook for running landmark sanity checks before validation.
 *
 * @returns {Object} - { isChecking, checkAnnotations, checkMultipleAnnotations, lastResult }
 */
export const useLandmarksSanityCheck = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const { addNotification, removeNotification } = useNotification();
  const notificationIdsRef = useRef([]);
  const { t } = useTranslation();

  // Pre-fetch all sanity check translations on hook initialization
  useEffect(() => {
    Object.values(ERROR_TITLE_KEYS).forEach(key => t(key));
    Object.values(ERROR_MESSAGE_KEYS).forEach(key => t(key));
  }, [t]);

  /**
   * Get translated error title by error ID
   */
  const getErrorTitle = errorId => {
    const key = ERROR_TITLE_KEYS[errorId];
    if (key) {
      const translated = t(key);
      if (translated) return translated;
    }
    return `Error ${errorId}`;
  };

  /**
   * Get translated error message with landmark names substituted.
   * Landmark names are transformed to user-friendly anatomical terms.
   */
  const getErrorMessage = (errorId, landmarkNames = []) => {
    const key = ERROR_MESSAGE_KEYS[errorId];
    const formattedNames = landmarkNames.map(formatLandmarkName);
    const names = formattedNames.length > 0 ? formattedNames.join(", ") : "";
    const translated = t(key) || "";
    return translated.replace("{names}", names);
  };

  /**
   * Clear all tracked sanity check notifications
   */
  const clearNotifications = useCallback(() => {
    notificationIdsRef.current.forEach(id => removeNotification(id));
    notificationIdsRef.current = [];
  }, [removeNotification]);

  /**
   * Show notifications for validation errors
   */
  const showErrorNotifications = useCallback(
    (errors, imageType = null) => {
      Object.entries(errors).forEach(([, errorList]) => {
        errorList.forEach(error => {
          const baseTitle = getErrorTitle(error.id);
          const title = imageType ? `${imageType}: ${baseTitle}` : baseTitle;
          const message = getErrorMessage(
            error.id,
            error.landmark_names || []
          );

          const id = addNotification(SnackbarType.ERROR, message, {
            title,
            duration: 0,
          });
          notificationIdsRef.current.push(id);
        });
      });
    },
    [addNotification, getErrorTitle, getErrorMessage]
  );

  /**
   * Run sanity checks on annotations.
   *
   * @param {Object} annotations - Annotations in anno_id_dict format
   * @param {string} [imageType] - Optional label for error messages (e.g., "Sagittal", "Coronal")
   * @returns {Promise<boolean>} - true if valid, false if errors found
   */
  const checkAnnotations = useCallback(
    async (annotations, imageType = null) => {
      if (!annotations || Object.keys(annotations).length === 0) {
        console.warn("[useLandmarksSanityCheck] No annotations to check");
        return true;
      }

      clearNotifications();
      setIsChecking(true);
      setLastResult(null);

      try {
        const result = await sanityCheckAnnotations(annotations);
        setLastResult(result);

        if (result.valid) {
          return true;
        }

        showErrorNotifications(result.errors, imageType);
        return false;
      } catch (error) {
        console.error("[useLandmarksSanityCheck] Error during check:", error);
        addNotification(
          SnackbarType.ERROR,
          `Failed to validate annotations: ${error.message}`,
          { title: "Validation Error", duration: 6000 }
        );
        // Return true to allow proceeding if sanity check service fails
        // (fail open - don't block user if service is down)
        return true;
      } finally {
        setIsChecking(false);
      }
    },
    [addNotification, clearNotifications, showErrorNotifications]
  );

  /**
   * Run sanity checks on multiple annotation sets (e.g., sagittal + coronal).
   * Does NOT clear notifications between checks - aggregates all errors.
   *
   * @param {Array<{annotations: Object, imageType: string}>} annotationSets - Array of annotation sets to check
   * @returns {Promise<boolean>} - true if ALL are valid, false if any have errors
   */
  const checkMultipleAnnotations = useCallback(
    async annotationSets => {
      if (!annotationSets || annotationSets.length === 0) {
        console.warn("[useLandmarksSanityCheck] No annotation sets to check");
        return true;
      }

      clearNotifications();
      setIsChecking(true);
      setLastResult(null);

      let allValid = true;
      const allResults = [];

      try {
        for (const { annotations, imageType } of annotationSets) {
          if (!annotations || Object.keys(annotations).length === 0) {
            continue;
          }

          const result = await sanityCheckAnnotations(annotations);
          allResults.push({ imageType, result });

          if (!result.valid) {
            showErrorNotifications(result.errors, imageType);
            allValid = false;
          } else {
          }
        }

        setLastResult(allResults);
        return allValid;
      } catch (error) {
        console.error("[useLandmarksSanityCheck] Error during check:", error);
        addNotification(
          SnackbarType.ERROR,
          `Failed to validate annotations: ${error.message}`,
          { title: "Validation Error", duration: 6000 }
        );
        return true; // fail open
      } finally {
        setIsChecking(false);
      }
    },
    [addNotification, clearNotifications, showErrorNotifications]
  );

  return {
    isChecking,
    checkAnnotations,
    checkMultipleAnnotations,
    lastResult,
  };
};

export default useLandmarksSanityCheck;
